import type { InvoiceFlag } from '@clockwise/clients/invoices';
import { describeInvoiceFlag, type InvoiceFlagSeverity } from '@clockwise/shared';
import { Flag } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * A set of review flags, spelled out.
 *
 * ONE component for both readers — the list's row popover and the invoice
 * detail panel. They describe the same rows, and two renderers would be two
 * places for the wording to drift; the whole point of storing structured
 * `facts` was that a flag reads the same everywhere.
 *
 * Every string comes from `describeInvoiceFlag`, the single formatter in
 * `@clockwise/shared`. Nothing here composes prose from `facts` itself — a
 * second place doing that is how the popover and the export end up disagreeing
 * about what changed.
 */

const GLYPH_CLASSES: Record<InvoiceFlagSeverity, string> = {
  info: 'text-muted-foreground',
  review: 'text-amber-600 dark:text-amber-400',
  blocking: 'text-destructive',
};

export function InvoiceFlagDetails({
  flags,
  className,
  glyphClassName,
}: {
  flags: readonly InvoiceFlag[];
  className?: string;
  /** One colour for every flag's glyph, in place of the per-severity one —
   *  for a reader that is already an alert (the editor's review strip). */
  glyphClassName?: string;
}) {
  // Not a spinner and not an empty box: a caller only renders this when it
  // knows there are flags, so "none" here means they failed to arrive rather
  // than that the invoice is clean. Saying so beats an empty panel that reads
  // as a clean bill of health (G15).
  if (flags.length === 0) {
    return (
      <p className={cn('text-muted-foreground text-xs', className)}>
        Couldn't load the flags for this invoice.
      </p>
    );
  }

  return (
    <ul className={cn('flex flex-col gap-2', className)}>
      {flags.map((flag) => {
        const described = describeInvoiceFlag(flag.facts);
        return (
          <li key={flag.id} className="flex gap-2">
            <Flag
              className={cn(
                'mt-0.5 size-3.5 shrink-0',
                glyphClassName ?? GLYPH_CLASSES[flag.severity],
              )}
              aria-hidden="true"
            />
            <div className="min-w-0">
              <p className="font-medium text-xs">{described.title}</p>
              {/* `break-words`, not `truncate`: a SOW amendment can list
                  several changed fields, and the detail is the part a reviewer
                  actually needs to read. */}
              <p className="break-words tabular-nums text-[11px] text-muted-foreground">
                {described.detail}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
